/**
 * Cloud Storage API Service
 * Connection management and folder/file browsing for Google Drive & Dropbox.
 */

import api from '../../../services/api';
import type { OAuthCallback } from '@/utils/oauthPopup';
import type { CloudConnection, CloudFolder, CloudFile } from '../types';

/** How finishing a connection ended (the server's `oauth_finish`). */
export type CloudOAuthOutcome = 'connected' | 'granted' | 'declined' | 'wrong_account' | 'error' | 'invalid_state';

const API_BASE = '/cloud-storage';

export class CloudStorageApi {
  // Connections
  static async listConnections(): Promise<CloudConnection[]> {
    const response = await api.get(`${API_BASE}/connections`);
    return response.data.connections;
  }

  /** The provider's consent URL and its `state`, which only this window may
   *  finish (utils/oauthPopup.ts). `params`: Google Drive's incremental
   *  consent — `access` (read | edit) and `mode: 'popup'` (googleAccessService). */
  static async startAuthorization(provider: string, params?: Record<string, string>
  ): Promise<{ authorization_url: string; state: string }> {
    const response = await api.get(`${API_BASE}/${provider}/oauth/authorize`, params ? { params } : undefined);
    return { authorization_url: response.data.authorization_url, state: response.data.state };
  }

  /** The provider's answer, handed back by this window as its own person. */
  static async finishAuthorization(provider: string, callback: OAuthCallback
  ): Promise<{ outcome: CloudOAuthOutcome; detail?: Record<string, string> }> {
    const response = await api.post(`${API_BASE}/${provider}/oauth/finish`, callback);
    return { outcome: response.data.outcome, detail: response.data.detail ? { error: response.data.detail } : undefined };
  }

  static async disconnect(provider: string): Promise<void> {
    await api.delete(`${API_BASE}/${provider}/connection`);
  }

  // Browsing
  static async listFolders(provider: string, parentId?: string): Promise<CloudFolder[]> {
    const params = parentId ? { parent_id: parentId } : {};
    const response = await api.get(`${API_BASE}/${provider}/folders`, { params });
    return response.data.folders;
  }

  static async listFiles(provider: string, folderId?: string): Promise<CloudFile[]> {
    const params = folderId ? { folder_id: folderId } : {};
    const response = await api.get(`${API_BASE}/${provider}/files`, { params });
    return response.data.files;
  }

  // Export / Download
  static async downloadAsDocx(content: string, fileName: string): Promise<void> {
    const response = await api.post(
      `${API_BASE}/export/download`,
      { content, file_name: fileName },
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.endsWith('.docx') ? fileName : `${fileName}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static async exportToCloud(
    provider: string,
    content: string,
    folderId: string,
    fileName: string
  ): Promise<{ file_id: string; name: string; web_view_link: string | null }> {
    const response = await api.post(`${API_BASE}/${provider}/export`, {
      content,
      folder_id: folderId,
      file_name: fileName,
    });
    return response.data.file;
  }

  // Default export folder
  static async getDefaultExportFolder(provider: string): Promise<{
    folder_id: string;
    folder_name: string;
    breadcrumbs: Array<{ id: string | null; name: string }>;
  } | null> {
    const response = await api.get(`${API_BASE}/export/default-folder`, {
      params: { provider },
    });
    return response.data.default_folder || null;
  }

  static async setDefaultExportFolder(
    provider: string,
    folderId: string,
    folderName: string,
    breadcrumbs: Array<{ id: string | null; name: string }>
  ): Promise<void> {
    await api.put(`${API_BASE}/export/default-folder`, {
      provider,
      folder_id: folderId,
      folder_name: folderName,
      breadcrumbs,
    });
  }
}
